/**
 * EtatDesLieuxResume — « dans quel état je prête cette salle ? », tranche 2 de 9.2.
 *
 * ─── LE MANQUE QU'IL COMBLE ─────────────────────────────────────────────────
 *
 * Au moment où le Service Technique valide une demande de salle, il atteste en
 * pratique que la salle est prêtable : propre, en état, conforme au dernier
 * état des lieux de sortie. Or l'écran ne lui montrait RIEN de cet état des
 * lieux. Il validait de mémoire, ou changeait d'écran et revenait.
 *
 * La donnée existait pourtant à un appel près : l'« état courant » d'un lieu est
 * la checklist du DERNIER état des lieux réalisé (mig 218), et il se met à jour
 * tout seul d'une location à la suivante.
 *
 * ─── CE QU'IL MONTRE, ET CE QU'IL TAIT ──────────────────────────────────────
 *
 * Les points qui ne sont PAS « ok », et eux seuls. Lister vingt points conformes
 * pour en cacher deux dégradés produirait exactement l'effet inverse de celui
 * recherché : l'agent survolerait. Quand tout est bon, une seule ligne le dit.
 *
 * Les notes internes et les coûts estimés ne sont pas affichés ici : ils vivent
 * sur la fiche de l'état des lieux, où l'agent est invité à aller.
 */
import { useEffect, useState } from 'react';
import { ClipboardList, TriangleAlert, CheckCircle2 } from 'lucide-react';
import { api } from '@/lib/api';
import type { EtatCourantLieu, ChecklistItem } from '@roronoa-collectivites/shared-types';

interface Props {
  /** Le bien réservé — c'est lui qui porte le rattachement au lieu. */
  materielId: string;
  /** Ouvre la fiche de l'état des lieux, ou l'écran des modèles s'il n'y en a pas. */
  onOuvrir: (etat: EtatCourantLieu) => void;
  /** Phrase de contexte, différente à la validation et au retour. */
  aide?: string;
}

const ETAT_LABEL: Record<string, string> = {
  a_surveiller: 'à surveiller',
  degrade: 'dégradé',
};

function formatDateFr(iso: string): string {
  return iso.split('-').reverse().join('/');
}

export function EtatDesLieuxResume({ materielId, onOuvrir, aide }: Props) {
  const [etat, setEtat] = useState<EtatCourantLieu | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    api.materiel.etatCourant(materielId)
      .then((e) => { if (vivant) setEtat(e); })
      .catch((e: unknown) => {
        if (vivant) setErreur(e instanceof Error ? e.message : 'Lecture impossible.');
      });
    return () => { vivant = false; };
  }, [materielId]);

  if (erreur) {
    return <p className="text-xs text-ink-subtle">État des lieux illisible ({erreur}).</p>;
  }
  if (!etat) return <p className="text-xs text-ink-subtle">Lecture de l’état des lieux…</p>;

  // Pas de lieu : la validation sera refusée par le serveur. On le dit ici
  // plutôt que de laisser l'agent découvrir l'erreur en cliquant.
  if (!etat.lieuId) {
    return (
      <div className="rounded-lg border border-urgent/40 bg-urgent-tint/30 p-3">
        <p className="flex items-start gap-1.5 text-sm text-urgent-fg">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          Cette salle n’est rattachée à aucun lieu : son état des lieux ne peut pas
          exister, et la validation sera refusée. Rattachez-la depuis sa fiche.
        </p>
      </div>
    );
  }

  const ecarts: ChecklistItem[] = etat.points.filter((p) => p.etat !== 'ok');
  const jamaisRealise = etat.edlId == null;

  return (
    <div className="rounded-lg border border-border/60 p-3">
      <p className="flex items-center gap-1.5 text-sm font-medium text-ink">
        <ClipboardList className="h-4 w-4" aria-hidden="true" />
        État des lieux — {etat.lieuNom ?? 'ce lieu'}
      </p>
      {aide && <p className="mt-0.5 text-xs text-ink-subtle">{aide}</p>}

      <p className="mt-1 text-xs text-ink-muted">
        {jamaisRealise
          ? 'Aucun état des lieux réalisé — le modèle du lieu fait foi, tous points conformes.'
          : `Dernier état des lieux du ${etat.realiseLe ? formatDateFr(etat.realiseLe) : '—'}.`}
      </p>

      {ecarts.length === 0 ? (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-success-fg">
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
          Tous les points sont conformes.
        </p>
      ) : (
        <ul className="mt-2 space-y-0.5">
          {ecarts.map((p) => (
            <li key={p.id} className="flex items-start gap-1.5 text-xs text-ink">
              <TriangleAlert
                className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${
                  p.etat === 'degrade' ? 'text-urgent-fg' : 'text-ink-muted'
                }`}
                aria-hidden="true"
              />
              <span>
                {p.libelle}
                <span className="text-ink-muted"> — {ETAT_LABEL[p.etat] ?? p.etat}</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={() => onOuvrir(etat)}
        className="mt-2 text-xs text-tech-fg hover:underline"
      >
        {jamaisRealise ? 'Créer l’état des lieux' : 'Modifier l’état des lieux'}
      </button>
    </div>
  );
}
